import { useTranslate } from "@tolgee/react";
import { useState } from "react";
import { toast } from "sonner";
import { Button } from "#/components/ui/button";
import {
	Dialog,
	DialogClose,
	DialogCloseButton,
	DialogContent,
	DialogTitle,
} from "#/components/ui/dialog";
import { WithdrawConfirmAlert } from "#/components/ui/withdraw-confirm-alert";
import {
	TokenChip,
	truncateAddress,
} from "#/features/trade/components/order-form/shared-fund-flow";
import { useAppSettings } from "#/hooks/use-app-settings";
import { useMe } from "#/hooks/use-me";
import { analytics } from "#/lib/analytics";
import { classifyWithdrawError } from "#/lib/api/referrals";
import { formatUsd, toNumber } from "#/lib/formatters";
import { useMyReferralStats } from "../hooks/use-my-referral-stats";
import { useMyWithdrawals } from "../hooks/use-my-withdrawals";
import { useWithdrawReferrals } from "../hooks/use-withdraw-referrals";

interface Props {
	enabled: boolean;
}

type DisabledReason =
	| "no-code"
	| "loading"
	| "below-minimum"
	| "inflight"
	| null;

export function ReferralEarningsCard({ enabled }: Props) {
	const { t } = useTranslate();
	const stats = useMyReferralStats();
	const withdrawals = useMyWithdrawals();
	const settings = useAppSettings();
	const [open, setOpen] = useState(false);

	const claimable = stats.data?.claimable_usd ?? "0";
	const lifetime = stats.data?.lifetime_earned_usd ?? "0";
	const referredCount = stats.data?.referred_count ?? 0;
	const claimableNum = toNumber(claimable);

	// Server is the source of truth for the minimum — we don't keep a
	// hardcoded constant. While settings are loading, gate the button
	// closed (the load is fast and no-auth, usually < 100ms).
	const minUsdStr = settings.data?.withdraw_min_usd;
	const minUsdNum = minUsdStr ? toNumber(minUsdStr) : null;

	// An "inflight" withdrawal is any pending or sent row. We block a
	// second sweep until it terminates — matches the server's inflight
	// check, so the button state stays honest even if the user reloads.
	const hasInflight = (withdrawals.data?.withdrawals ?? []).some(
		(w) => w.status === "pending" || w.status === "sent",
	);

	const reason: DisabledReason = !enabled
		? "no-code"
		: minUsdNum === null
			? "loading"
			: hasInflight
				? "inflight"
				: claimableNum < minUsdNum
					? "below-minimum"
					: null;

	const disabled = reason !== null;

	function onClick() {
		if (disabled) return;
		setOpen(true);
	}

	return (
		<section>
			<h2 className="text-headline text-foreground">
				{t("referrals.earnings.title")}
			</h2>
			<p className="mt-1 text-label2 text-muted-foreground">
				{t("referrals.earnings.update-interval")}
			</p>

			<div className="mt-4 grid gap-4 sm:grid-cols-3">
				<Stat
					label={t("referrals.earnings.claimable")}
					value={formatUsd(claimable)}
					emphasized
				/>
				<Stat
					label={t("referrals.earnings.lifetime")}
					value={formatUsd(lifetime)}
				/>
				<Stat
					label={t("referrals.earnings.referred")}
					value={String(referredCount)}
				/>
			</div>

			<div className="mt-5 flex items-center justify-end gap-3">
				{reason ? (
					<p
						className="text-label2 text-muted-foreground"
						data-testid="withdraw-reason"
					>
						{reasonMessage(reason, minUsdStr, t)}
					</p>
				) : null}
				<Button
					type="button"
					onClick={onClick}
					disabled={disabled}
					// Use foreground/background tokens (not primary) so the
					// button auto-inverts with the theme: dark-on-light in
					// light mode, light-on-dark in dark mode. The default
					// `primary` variant is the brand teal, which lands as
					// low-contrast washed-out in both modes here.
					className="bg-foreground text-background hover:bg-foreground/90"
				>
					{t("referrals.earnings.withdraw")}
				</Button>
			</div>

			<WithdrawDialog
				open={open}
				onOpenChange={setOpen}
				claimable={claimable}
			/>
		</section>
	);
}

// reasonMessage maps the disabled state to the inline hint shown next to
// the button. Keeping this out of the component body keeps the JSX flat.
function reasonMessage(
	reason: Exclude<DisabledReason, null>,
	minUsd: string | undefined,
	t: (key: string, params?: Record<string, string>) => string,
): string {
	switch (reason) {
		case "no-code":
			return t("referrals.earnings.withdraw.reason-no-code");
		case "loading":
			return t("referrals.earnings.withdraw.reason-loading");
		case "below-minimum":
			return t("referrals.earnings.withdraw.below-minimum", {
				min: formatUsd(minUsd ?? "0"),
			});
		case "inflight":
			return t("referrals.earnings.withdraw.inflight");
	}
}

function WithdrawDialog({
	open,
	onOpenChange,
	claimable,
}: {
	open: boolean;
	onOpenChange: (next: boolean) => void;
	claimable: string;
}) {
	const { t } = useTranslate();
	const me = useMe();
	const mutation = useWithdrawReferrals();
	const [error, setError] = useState<string | null>(null);
	const [showConfirm, setShowConfirm] = useState(false);

	function handleOpenChange(next: boolean) {
		if (next) setError(null);
		if (!next) setShowConfirm(false);
		onOpenChange(next);
	}

	function handleSendClick() {
		setShowConfirm(true);
	}

	function handleConfirm() {
		setShowConfirm(false);
		setError(null);
		mutation.mutate(undefined, {
			onSuccess: (res) => {
				toast.success(
					t("referrals.earnings.withdraw.success", {
						amount: formatUsd(res.amount_usd),
					}),
				);
				onOpenChange(false);
				analytics.track("referral_withdraw_submitted", {
					amount_usd: toNumber(res.amount_usd),
				});
			},
			onError: (err) => {
				const kind = classifyWithdrawError(err);
				const key =
					kind === "below-minimum"
						? "referrals.earnings.withdraw.error-below-minimum"
						: kind === "inflight"
							? "referrals.earnings.withdraw.error-inflight"
							: "referrals.earnings.withdraw.error-generic";
				setError(t(key));
			},
		});
	}

	const wallet = me.data?.wallet_address ?? null;

	// Visual language mirrors the deposit dialog
	// (src/features/trade/components/order-form/deposit-dialog.tsx):
	// gradient surface + trade-* tokens + TokenChip pills + a single
	// primary CTA in the brand color. The withdraw flow is single-step
	// (no FlowIndicator) — the only on-screen work is confirming the
	// sweep — so we keep the body minimal.
	return (
		<Dialog open={open} onOpenChange={handleOpenChange}>
			<DialogContent className="flex max-h-[calc(100dvh-1rem)] w-[min(440px,calc(100vw-1rem))] flex-col overflow-hidden rounded-[22px] border border-trade-border-subtle bg-[linear-gradient(180deg,var(--trade-surface-overlay),var(--trade-surface-overlay-end))] p-0 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.03),0_36px_72px_rgba(0,0,0,0.52)] sm:max-h-[calc(100dvh-2rem)] sm:w-[min(440px,calc(100vw-2rem))]">
				<div className="flex items-start justify-between gap-4 px-5 pt-5">
					<div className="min-w-0">
						<DialogTitle className="text-title1 text-trade-text-strong">
							{t("referrals.earnings.withdraw.confirm-title")}
						</DialogTitle>
						<p className="mt-1 text-label2-medium text-trade-text-secondary">
							{t("referrals.earnings.withdraw.confirm-description", {
								amount: formatUsd(claimable),
							})}
						</p>
						<div className="mt-3 flex flex-wrap items-center gap-2">
							<TokenChip
								iconSrc="https://sdk-cdn.fun.xyz/images/usdc.svg"
								label="USDC"
							/>
							<TokenChip
								iconSrc="https://sdk-cdn.fun.xyz/images/arbitrum.svg"
								label="Arbitrum"
							/>
							{wallet ? (
								<span className="truncate rounded-full bg-trade-surface-inset px-2 py-1 text-caption1-medium text-trade-text-secondary">
									{truncateAddress(wallet)}
								</span>
							) : null}
						</div>
					</div>
					<DialogCloseButton disabled={mutation.isPending} />
				</div>

				<div className="min-h-0 overflow-y-auto px-5 pb-5 pt-4">
					<div className="rounded-[16px] border border-trade-border-subtle bg-trade-surface-inset px-4 py-3">
						<dl className="space-y-3 text-label2">
							<div className="flex items-center justify-between gap-3">
								<dt className="text-trade-text-secondary">
									{t("referrals.earnings.withdraw.amount-label")}
								</dt>
								<dd className="text-headline tabular-nums text-trade-text-strong">
									{formatUsd(claimable)}
								</dd>
							</div>
							<div className="flex items-center justify-between gap-3 border-t border-trade-border-subtle pt-3">
								<dt className="text-trade-text-secondary">
									{t("referrals.earnings.withdraw.destination-label")}
								</dt>
								<dd className="text-label2-medium text-trade-text-strong">
									{wallet ? truncateAddress(wallet) : "—"}
								</dd>
							</div>
							<div className="flex items-center justify-between gap-3 border-t border-trade-border-subtle pt-3">
								<dt className="text-trade-text-secondary">
									{t("referrals.earnings.withdraw.network-label")}
								</dt>
								<dd className="text-label2-medium text-trade-text-strong">
									Arbitrum One
								</dd>
							</div>
						</dl>
					</div>

					<p className="mt-4 text-label2 text-trade-text-secondary">
						{t("referrals.earnings.withdraw.payout-notice")}
					</p>

					{error ? (
						<p className="mt-2 text-label2 text-destructive">{error}</p>
					) : null}

					<div className="mt-4 flex gap-2">
						<DialogClose asChild>
							<button
								type="button"
								disabled={mutation.isPending}
								className="inline-flex min-h-12 flex-1 items-center justify-center rounded-[14px] border border-trade-border-subtle bg-trade-surface text-body2-bold text-trade-text-strong transition-colors hover:bg-trade-interactive disabled:opacity-50"
							>
								{t("referrals.earnings.withdraw.cancel")}
							</button>
						</DialogClose>
						<button
							type="button"
							onClick={handleSendClick}
							disabled={mutation.isPending}
							className="inline-flex min-h-12 flex-1 items-center justify-center gap-2 rounded-[14px] bg-primary text-body2-bold text-primary-foreground transition-[filter,transform,opacity] hover:brightness-105 disabled:opacity-50"
						>
							{mutation.isPending
								? t("referrals.earnings.withdraw.submitting")
								: t("referrals.earnings.withdraw.confirm")}
						</button>
					</div>
				</div>
			</DialogContent>

			<WithdrawConfirmAlert
				open={showConfirm}
				onOpenChange={setShowConfirm}
				amount={formatUsd(claimable)}
				address={wallet ? truncateAddress(wallet) : "—"}
				onConfirm={handleConfirm}
			/>
		</Dialog>
	);
}

function Stat({
	label,
	value,
	emphasized,
}: {
	label: string;
	value: string;
	emphasized?: boolean;
}) {
	return (
		<div>
			<p className="text-label2-medium text-muted-foreground">{label}</p>
			<p
				className={
					emphasized
						? "mt-1 text-title1 text-foreground"
						: "mt-1 text-title1 text-foreground"
				}
			>
				{value}
			</p>
		</div>
	);
}
